import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = sessionStorage.getItem("mira-chat-session");
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem("mira-chat-session", sid);
  }
  return sid;
};

export default function ChatWidget() {
  const { t, lang } = useLang();
  const c = t.chatWidget;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  const sendText = async (text) => {
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: getSessionId(), message: text, lang });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: c.error }]);
    } finally {
      setLoading(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    await sendText(text);
  };

  return (
    <>
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-5 right-5 z-[85] flex h-14 w-14 items-center justify-center rounded-full text-white shadow-[0_16px_32px_-12px_rgba(198,156,109,0.7)] transition-transform duration-300 hover:scale-105"
        style={{ background: "var(--camel)", border: "2px solid var(--gold)" }}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[85] flex w-[calc(100vw-2.5rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border shadow-[0_32px_64px_-24px_rgba(44,39,37,0.35)]"
            style={{ background: "var(--surface)", borderColor: "var(--line)", height: "min(540px, 70vh)" }}
            data-testid="chat-panel"
          >
            <div className="border-b px-6 py-4" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
              <p className="font-serif-display text-xl font-semibold">✦ {c.title}</p>
              <p className="text-xs" style={{ color: "var(--muted-warm)" }}>{c.subtitle}</p>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="chat-messages">
              <div className="max-w-[85%] rounded-2xl rounded-tl-md px-4 py-3 text-sm leading-relaxed" style={{ background: "var(--surface-secondary)" }} data-testid="chat-greeting">
                {c.greeting}
              </div>
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-quick-questions">
                  {c.quick.map((q, i) => (
                    <button
                      key={i}
                      data-testid={`chat-quick-${i + 1}`}
                      onClick={() => sendText(q)}
                      disabled={loading}
                      className="rounded-full border px-3.5 py-2 text-xs font-medium transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)] disabled:opacity-50"
                      style={{ borderColor: "var(--gold)", color: "var(--on-surface)" }}
                    >
                      ✦ {q}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto rounded-tr-md text-white" : "rounded-tl-md"}`}
                  style={m.role === "user" ? { background: "var(--camel)" } : { background: "var(--surface-secondary)" }}
                >
                  {m.content}
                </div>
              ))}
              {loading && (
                <div className="flex max-w-[85%] items-center gap-2 rounded-2xl rounded-tl-md px-4 py-3" style={{ background: "var(--surface-secondary)" }} data-testid="chat-loading">
                  <Loader2 size={14} className="animate-spin" style={{ color: "var(--camel)" }} />
                  <span className="text-xs" style={{ color: "var(--muted-warm)" }}>…</span>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="flex items-center gap-2 border-t px-4 py-3" style={{ borderColor: "var(--line)" }}>
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={c.placeholder}
                maxLength={1000}
                className="flex-1 rounded-full border bg-white px-4 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-[var(--camel)]"
                style={{ borderColor: "var(--line)" }}
              />
              <button
                type="submit"
                data-testid="chat-send-btn"
                disabled={loading || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-50"
                style={{ background: "var(--camel)" }}
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
